import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Menu,
  Sparkles,
  Gem,
  ChevronDown,
  Quote,
  Users,
  Leaf,
  Award,
  Heart,
  Clock,
  Shield,
  Star,
} from "lucide-react";
import NavBar from "../components/Navbar/NavBar";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";

const AroundWorld = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      setIsVisible(currentScrollPos > 100);

      const sections = [
        "hero",
        "about",
        // products moved to dedicated page
        "quality",
        "process",
        "testimonials",
        "tips",
      ];
      const sectionElements = sections.map((id) => document.getElementById(id));
      const currentSection = sectionElements.findIndex((el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return (
          rect.top <= window.innerHeight / 2 &&
          rect.bottom >= window.innerHeight / 2
        );
      });
      if (currentSection !== -1) setActiveSection(sections[currentSection]);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % oliveTips.length);
    }, 5000);
    return () => clearInterval(tipInterval);
  }, []);

  // Products moved to dedicated /products page

  const qualityServices = [
    {
      icon: Gem,
      title: "Premium Quality",
      description:
        "Strict selection of olives and modern cold-press techniques.",
    },
    {
      icon: Leaf,
      title: "Traditional Methods",
      description:
        "Combining ancestral know-how with state-of-the-art production.",
    },
    {
      icon: Shield,
      title: "Sustainability",
      description: "Eco-friendly farming practices and respect for nature.",
    },
    {
      icon: Award,
      title: "Award Winning",
      description:
        "Recognized internationally for excellence in olive oil production.",
    },
  ];

  const processSteps = [
    {
      step: "1",
      title: "Harvesting",
      description:
        "Hand-picked olives at the perfect ripeness to ensure premium quality.",
    },
    {
      step: "2",
      title: "Cleaning",
      description:
        "Thorough washing to remove leaves, twigs, and other impurities.",
    },
    {
      step: "3",
      title: "Cold Pressing",
      description:
        "Extracted within hours of harvesting to preserve flavor and nutrients.",
    },
    {
      step: "4",
      title: "Decantation",
      description:
        "Natural separation of oil from water and solids without chemicals.",
    },
    {
      step: "5",
      title: "Filtration",
      description:
        "Gentle filtration to maintain quality while removing sediment.",
    },
    {
      step: "6",
      title: "Bottling",
      description:
        "Dark glass bottles to protect from light and preserve freshness.",
    },
  ];

  const testimonials = [
    {
      name: "Chef Marco B.",
      role: "Michelin Star Chef",
      content:
        "Huilerie Chelbi's olive oil transforms my dishes. The complexity and depth of flavor are unmatched.",
    },
    {
      name: "Sophie L.",
      role: "Food Blogger",
      content:
        "I've tried olive oils from across the Mediterranean, but none compare to the quality and taste of Chelbi's products.",
    },
    {
      name: "Ahmed T.",
      role: "Local Farmer",
      content:
        "For generations, our family has supplied olives to Solivera. Their commitment to quality and fair practices is exceptional.",
    },
  ];

  const oliveTips = [
    {
      title: "How to Store Olive Oil",
      content:
        "Keep your olive oil in a cool, dark place to preserve its flavor and nutrients. Avoid storing near heat sources.",
    },
    {
      title: "Cooking with Olive Oil",
      content:
        "Discover the best ways to use extra virgin olive oil for cooking and finishing dishes. Different varieties suit different cooking methods.",
    },
    {
      title: "Health Benefits of Olive Oil",
      content:
        "Learn about the nutritional advantages and antioxidant properties of olive oil, including heart health and anti-inflammatory benefits.",
    },
    {
      title: "Tasting Olive Oil",
      content:
        "Proper tasting involves assessing the fruitiness, bitterness, and pungency. Look for balanced complexity in quality oils.",
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const staggerChildren = {
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Structured Data for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Solivera",
    description:
      "Premium extra virgin olive oil producer from Kairouan, Tunisia. Four generations of olive oil craftsmanship combining tradition with modern excellence.",
    url: "https://www.solivera-oil.com",
    logo: "https://www.solivera-oil.com/logosol.svg",
    foundingDate: "1987",
    founders: [
      {
        "@type": "Person",
        name: "Chelbi Family",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Kairouan",
      addressCountry: "Tunisia",
    },
    sameAs: [],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "150",
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Solivera - Premium Olive Oil",
    url: "https://www.solivera-oil.com",
    description:
      "Discover Solivera's premium extra virgin olive oils from Tunisia. Handcrafted with tradition, quality, and sustainability.",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.solivera-oil.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "About Solivera",
        item: "https://www.solivera-oil.com/AroundWorld",
      },
    ],
  };

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Solivera Premium Olive Oil",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: testimonials.length.toString(),
    },
    review: testimonials.map((testimonial, index) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: testimonial.name,
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
      },
      reviewBody: testimonial.content,
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Artisanal Olive Oil Production Process",
    description:
      "Learn about Solivera's traditional six-step process for producing premium extra virgin olive oil",
    step: processSteps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.title,
      text: step.description,
    })),
  };

  return (
    <>
      <Helmet>
        {/* Primary SEO Meta Tags */}
        <title>
          Solivera - Premium Olive Oil Excellence from Kairouan, Tunisia | About Us
        </title>
        <meta
          name="description"
          content="Discover Solivera's four generations of olive oil craftsmanship. Premium extra virgin olive oils from Kairouan, Tunisia. Handcrafted with traditional methods, modern excellence, and sustainable practices. Award-winning quality since 1987."
        />
        <meta
          name="keywords"
          content="Solivera, premium olive oil, extra virgin olive oil Tunisia, Kairouan olive oil, traditional olive oil, cold-pressed olive oil, sustainable olive farming, award-winning olive oil, Tunisian olive oil, olive oil production, artisanal olive oil, organic olive oil"
        />
        <meta name="author" content="Solivera" />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <link rel="canonical" href="https://www.solivera-oil.com/AroundWorld" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="geo.region" content="TN-21" />
        <meta name="geo.placename" content="Kairouan, Tunisia" />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Solivera - Premium Olive Oil Excellence from Tunisia"
        />
        <meta
          property="og:description"
          content="Four generations of olive oil craftsmanship. Premium extra virgin olive oils from Kairouan, Tunisia, combining traditional methods with modern excellence."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.solivera-oil.com/AroundWorld" />
        <meta
          property="og:image"
          content="https://www.solivera-oil.com/images/olive-oil-premium.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Solivera Premium Olive Oil - Four Generations of Excellence"
        />
        <meta property="og:site_name" content="Solivera" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Solivera - Premium Olive Oil Excellence from Tunisia"
        />
        <meta
          name="twitter:description"
          content="Four generations of olive oil craftsmanship. Premium extra virgin olive oils from Kairouan, Tunisia."
        />
        <meta
          name="twitter:image"
          content="https://www.solivera-oil.com/images/olive-oil-premium.jpg"
        />
        <meta name="twitter:image:alt" content="Solivera Premium Olive Oil" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-[#fff6e3] text-black overflow-hidden">
        <NavBar />


      {/* Hero Section */}
      <section
        id="hero"
        aria-label="Hero section - Solivera Premium Olive Oil"
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg.jpg")}
            alt="Premium olive oil production at Solivera, Kairouan Tunisia - Traditional olive groves and modern processing facilities"
            className="absolute inset-0 w-full h-full object-center"
            loading="eager"
            fetchPriority="high"
            width="1920"
            height="1080"
          />
          {/* Overlay Colors for Luxury Effect */}
          <div className="absolute inset-0 bg-[#272e02]/30"></div>
          <div className="absolute inset-0 bg-[#fff6e3]/10 animate-pulse"></div>

          {/* Floating Blurred Circles */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-40 h-40 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/3 right-1/3 w-32 h-32 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.2, 0.4],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="container mx-auto text-center relative z-10"
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="mx-auto w-16 sm:w-20 h-16 sm:h-20 mb-6"
          >
            <Sparkles className="w-full h-full text-[#225601]" />
          </motion.div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold leading-tight mb-6 tracking-wide text-white drop-shadow-lg">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1 }}
            >
              Solivera – Pure Olive Excellence
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-8 leading-relaxed px-4 text-white/90 drop-shadow-md font-light"
          >
            From the fertile lands of Kairouan, Tunisia, we produce premium
            olive oils that honor tradition, sustainability, and excellence.
          </motion.p>

          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="products"
            aria-label="Explore Solivera premium olive oil products"
                className="group inline-flex items-center px-6 sm:px-12 py-3 sm:py-4 text-base sm:text-lg font-semibold text-[#272e02] bg-white/95 backdrop-blur-sm border-2 border-[#272e02] rounded-full hover:bg-[#272e02] hover:text-white transition-all duration-300 mb-12 shadow-lg hover:shadow-xl hover:shadow-[#272e02]/20"
          >
            <span className="relative z-10">Explore Our Products</span>
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
          </motion.a>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <article
        id="about"
        aria-labelledby="about-heading"
        className="py-16 sm:py-32 relative px-4 sm:px-6"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg2.jpg")}
            alt="Solivera olive oil production facility and olive groves in Kairouan, Tunisia - showcasing traditional and modern production methods"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width="1920"
            height="1080"
          />
          {/* Overlay Colors for Luxury Effect */}
          <div className="absolute inset-0 bg-[#272e02]/30"></div>
          <div className="absolute inset-0 bg-[#fff6e3]/10 animate-pulse"></div>

          {/* Optional floating blurred elements */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-24 h-24 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.2, 0.4],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* About Content */}
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 sm:p-16 text-center border border-black/10 shadow-2xl hover:shadow-3xl transition-shadow duration-500"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#fff6e3] mb-8"
            >
              <Users className="w-10 h-10 text-[#225601]" />
            </motion.div>

            <h2
              id="about-heading"
              className="text-4xl sm:text-5xl font-bold mb-8 sm:mb-12 text-black drop-shadow-lg"
            >
              About Solivera
            </h2>

            <div className="text-base sm:text-xl max-w-4xl mx-auto mb-8 leading-relaxed text-black/90 drop-shadow-md space-y-4">
              <p>
                Established in 1987, Solivera represents four generations
                of olive oil craftsmanship. Our olive oils are a family tradition,
                carefully crafted from hand-picked olives using both traditional
                and modern methods.
              </p>
              <p>
                Every drop reflects the richness of Tunisian
                heritage. We embrace sustainability and modern innovation while
                honoring centuries-old techniques that have been passed down
                through generations.
              </p>
            </div>

            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12"
              variants={staggerChildren}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { value: "4+", label: "Generations" },
                { value: "1000+", label: "Hectares" },
                { value: "30+", label: "Awards" },
                { value: "15+", label: "Countries" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                className="p-4 bg-gradient-to-br from-[#fff6e3] to-white rounded-xl border border-[#225601]/10 hover:border-[#225601]/30 transition-all duration-300 hover:shadow-lg"
              >
                  <div className="text-2xl md:text-3xl font-bold text-[#225601]">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-black/70">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </article>

      {/* Products section moved to dedicated /products page */}

      {/* Quality Section */}
      <section
        id="quality"
        aria-labelledby="quality-heading"
        className="py-16 sm:py-32 px-4 sm:px-6 relative"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg2.jpg")}
            alt="Solivera quality control and premium olive oil certification process - showcasing commitment to excellence"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width="1920"
            height="1080"
          />
          {/* Overlay Colors for Depth */}
          <div className="absolute inset-0 bg-[#225601]/20"></div>
          <div className="absolute inset-0 bg-white/10 animate-pulse"></div>

          {/* Floating blurred elements */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-24 h-24 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-20 h-20 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.2, 0.4],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* Content */}
        <div className="container mx-auto relative z-10">
          <h2
            id="quality-heading"
            className="text-4xl sm:text-5xl font-bold mb-12 text-center text-white drop-shadow-lg"
          >
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Our Commitment to Quality
            </motion.span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {qualityServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group rounded-2xl bg-white/95 backdrop-blur-sm p-6 sm:p-8 border border-black/10 shadow-lg hover:shadow-2xl hover:border-[#225601]/20 transition-all duration-500 flex flex-col items-center text-center"
              >
                <motion.div
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-[#fff6e3] to-[#225601]/10 flex items-center justify-center mb-4 group-hover:from-[#225601]/10 group-hover:to-[#fff6e3] transition-all duration-500"
                  whileHover={{ rotate: 360, scale: 1.15 }}
                  transition={{ duration: 0.6 }}
                >
                  <service.icon
                    className="w-8 h-8 text-[#225601]"
                    strokeWidth={1.5}
                  />
                </motion.div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-black">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-base text-black/80">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        id="process"
        aria-labelledby="process-heading"
        className="py-16 sm:py-32 px-4 sm:px-6 relative"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg2.jpg")}
            alt="Solivera artisanal olive oil production process - cold pressing, filtration, and bottling stages"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-[#225601]/20"></div>
          <div className="absolute inset-0 bg-white/10 animate-pulse"></div>

          {/* Floating blurred elements */}
          <motion.div
            className="absolute top-1/3 left-1/4 w-24 h-24 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-20 h-20 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.2, 0.4] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* Content */}
        <div className="container mx-auto relative z-10">
          <h2
            id="process-heading"
            className="text-4xl sm:text-5xl font-bold mb-12 text-center text-white drop-shadow-lg"
          >
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Our Artisanal Process
            </motion.span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-black/10 hover:shadow-xl hover:border-[#225601]/20 transition-all duration-300 group"
              >
                <div className="text-5xl font-bold text-[#225601]/30 mb-4 group-hover:text-[#225601]/50 transition-colors duration-300">
                  {step.step}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 text-black group-hover:text-[#225601] transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-black/70 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="testimonials"
        aria-labelledby="testimonials-heading"
        className="py-16 sm:py-32 px-4 sm:px-6 relative"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg.jpg")}
            alt="Customer testimonials and reviews for Solivera premium olive oil - trusted by chefs and food enthusiasts worldwide"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-[#225601]/20"></div>
          <div className="absolute inset-0 bg-white/10 animate-pulse"></div>

          {/* Floating blurred elements */}
          <motion.div
            className="absolute top-1/4 left-1/3 w-24 h-24 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/3 w-20 h-20 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.2, 0.4] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* Content */}
        <div className="container mx-auto relative z-10">
          <h2
            id="testimonials-heading"
            className="text-4xl sm:text-5xl font-bold mb-12 text-center text-white drop-shadow-lg"
          >
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              What Our Customers Say
            </motion.span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.article
                key={index}
                itemScope
                itemType="https://schema.org/Review"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-black/10 hover:shadow-xl hover:border-[#225601]/20 transition-all duration-300 group"
              >
                <Quote className="w-10 h-10 text-[#225601]/30 mb-4 group-hover:text-[#225601]/50 transition-colors duration-300" />
                <p
                  itemProp="reviewBody"
                  className="text-black/80 italic mb-6 leading-relaxed text-base sm:text-lg"
                >
                  "{testimonial.content}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                      className="font-bold text-black"
                    >
                      <span itemProp="name">{testimonial.name}</span>
                    </div>
                    <div className="text-sm text-black/60">{testimonial.role}</div>
                  </div>
                  <div
                    itemProp="reviewRating"
                    itemScope
                    itemType="https://schema.org/Rating"
                    className="flex items-center"
                  >
                    <span itemProp="ratingValue" className="sr-only">5</span>
                    <div className="flex text-[#225601]" aria-label="5 out of 5 stars">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-4 h-4 fill-current"
                          strokeWidth={1.5}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section
        id="tips"
        aria-labelledby="tips-heading"
        className="py-16 sm:py-32 px-4 sm:px-6 relative"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={require("../images/bg.jpg")}
            alt="Olive oil tips and insights - expert advice on storage, cooking, health benefits, and tasting premium olive oil"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-[#225601]/20"></div>
          <div className="absolute inset-0 bg-white/10 animate-pulse"></div>

          {/* Floating blurred elements */}
          <motion.div
            className="absolute top-1/3 left-1/4 w-24 h-24 rounded-full bg-[#225601]/10 filter blur-xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-20 h-20 rounded-full bg-[#d4a574]/10 filter blur-xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.2, 0.4] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        {/* Content */}
        <div className="container mx-auto relative z-10">
          <h2
            id="tips-heading"
            className="text-4xl sm:text-5xl font-bold mb-12 text-center text-white drop-shadow-lg"
          >
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Olive Oil Tips & Insights
            </motion.span>
          </h2>

          <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-black/10 relative min-h-[300px] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTip}
                id={`tip-${currentTip}`}
                role="tabpanel"
                aria-labelledby={`tip-tab-${currentTip}`}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 p-6 sm:p-8"
              >
                <div className="flex items-start mb-4 sm:mb-6">
                  <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-[#225601] mr-3 mt-1 flex-shrink-0" />
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-black leading-tight">
                    {oliveTips[currentTip].title}
                  </h3>
                </div>
                <p className="text-black/80 text-base sm:text-lg md:text-xl leading-relaxed">
                  {oliveTips[currentTip].content}
                </p>
              </motion.div>
            </AnimatePresence>

            <div
              className="absolute bottom-6 left-6 flex space-x-2"
              role="tablist"
              aria-label="Olive oil tips navigation"
            >
              {oliveTips.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTip(index)}
                  role="tab"
                  aria-selected={currentTip === index}
                  aria-controls={`tip-${index}`}
                  aria-label={`View tip ${index + 1}: ${oliveTips[index].title}`}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentTip === index
                      ? "bg-[#225601] scale-125"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to Top */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Scroll to top of page"
        className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 p-3 sm:p-4 rounded-full bg-black/70 hover:bg-black/90 transition-all duration-300 border border-black shadow-lg z-40 focus:outline-none focus:ring-2 focus:ring-[#225601] focus:ring-offset-2"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 rotate-[-90deg] text-white" />
      </motion.button>
      </main>
    </>
  );
};

export default AroundWorld;
